import { useEffect, useState, useMemo } from "react";
import { Header } from "../../Header";
import { Loading } from "../../Loading";
import { Error } from "../../Error";

import { Container, Content, Tittle } from "./style";

import {
  AnalisesRelatorio,
  ClientReports,
} from "../../../models/reportsClient";
import { apiOperation, headers } from "../../../services/api";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  ListItem,
  Chip,
  IconButton,
  Modal,
  Tabs,
  Tab,
  Stack,
  Divider,
  Tooltip,
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { cnpjOrCpfMask } from "../../../utils/mask";
import moment from "moment";
import { filters, IFilter } from "./models";
import { Close, FilterList, Assessment, FilterAlt } from "@mui/icons-material";
import { ContentModal, HeaderModal, SubTitle } from "../../SearchHistory/style";
import { FilterForm } from "./FilterForm";

interface ChipData {
  id: string;
  value: string;
}

// Estilos modernos reaproveitáveis sem cores literais
const modernCardStyle = {
  p: { xs: 2, sm: 3 },
  borderRadius: 4,
  boxShadow: 1,
  border: "1px solid",
  borderColor: "divider",
  backgroundColor: "background.paper",
};

const accordionStyle = {
  mb: 2,
  borderRadius: "16px !important",
  boxShadow: 2,
  border: "1px solid",
  borderColor: "divider",
  "&:before": { display: "none" },
  overflow: "hidden",
  transition: "all 0.2s ease-in-out",
};

const tableHeaderStyle = {
  fontWeight: 700,
  color: "text.secondary",
  textTransform: "uppercase" as const,
  fontSize: "0.75rem",
  letterSpacing: "0.5px",
  backgroundColor: "action.hover",
};

export const SearchManager = () => {
  const [loadingModal, setLoadingModal] = useState<boolean>(false);
  const [clientReports, setClientReports] = useState<ClientReports>();
  const [expanded, setExpanded] = useState<string | false>("");
  const [dataFilter, setDataFilter] = useState<IFilter>({ ...filters });
  const [chipDataList, setChipDataList] = useState<ChipData[]>([]);
  const [open, setOpen] = useState(false);
  const [showLoading, setShowLoading] = useState(true);

  const [activeTab, setActiveTab] = useState(0);

  const [errorApi, setErrorApi] = useState({
    message: "",
    title: "",
    state: false,
  });

  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel : false);
    };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setExpanded(false);
  };

  const getUrlsReports = async (params = "") => {
    try {
      const config = { headers: headers() };
      const { data } = await apiOperation.get<string[]>(
        `administracao/relatorio${params}`,
        config,
      );
      return data ?? [];
    } catch (error: any) {
      setErrorApi({
        title: `API indisponível`,
        message: `Falha ao gerar relatórios!`,
        state: true,
      });
      return [];
    }
  };

  const { totalNoTouch, totalFlex } = useMemo(() => {
    if (!clientReports?.relatorioClientes)
      return { totalNoTouch: 0, totalFlex: 0 };

    return clientReports.relatorioClientes.reduce(
      (acc, client) => {
        acc.totalNoTouch += client.analisesNoTouch.length;
        acc.totalFlex += client.analisesFlex.length;
        return acc;
      },
      { totalNoTouch: 0, totalFlex: 0 },
    );
  }, [clientReports]);

  const handleClose = () => setOpen(false);
  const handleOpen = () => setOpen(true);

  function searchFilter(item: any) {
    const listFilters: ChipData[] = [];
    Object.keys(item).forEach((key) => {
      if (item[key]) {
        listFilters.push({
          id: key,
          value: item[key],
        });
      }
    });
    setDataFilter({ ...item });
    setChipDataList(listFilters);
    setOpen(false);
    get(mountParams(item, activeTab));
  }

  const get = async (params = "") => {
    try {
      setShowLoading(true);
      const { data } = await apiOperation.get<ClientReports>(
        `administracao/obter${params}`,
        { headers: headers() },
      );
      setClientReports(data);
    } catch (error: any) {
      setErrorApi({
        title: `API indisponível`,
        message: `Falha ao buscar dados!`,
        state: true,
      });
    } finally {
      setShowLoading(false);
    }
  };

  function deleteFilter(item: any) {
    if (item.id.includes("data")) {
      const newChipDataList = chipDataList.filter(
        (item) => !item.id.includes("data"),
      );
      setChipDataList([...newChipDataList]);
      setDataFilter({ ...dataFilter, dataFinal: "", dataInicial: "" });
    } else {
      const index = chipDataList.findIndex((chip) => chip.id === item.id);
      chipDataList.splice(index, 1);
      setChipDataList([...chipDataList]);
      setDataFilter({ ...dataFilter, [item.id]: "" });
    }
  }

  const mountParams = (filter: IFilter, activeTabIndex: number): string => {
    const params = new URLSearchParams();

    if (filter.dataInicial) {
      params.append(
        "dataInicial",
        moment(filter.dataInicial).format("YYYY-MM-DD"),
      );
    }
    if (filter.dataFinal) {
      params.append("dataFinal", moment(filter.dataFinal).format("YYYY-MM-DD"));
    }

    const reportType = activeTabIndex === 0 ? "NoTouch" : "Flex";
    params.append("tipoRelatorio", reportType);

    return params.toString() ? `?${params.toString()}` : "";
  };

  const onGenerateReport = async () => {
    const params = mountParams(dataFilter, activeTab);
    const urlDonwloadReports = (await getUrlsReports(params)) || [];

    if (!urlDonwloadReports || urlDonwloadReports.length === 0) {
      setErrorApi({
        title: `Nenhum relatório foi encontrado`,
        message: `Não há dados para gerar relatório para a visualização atual com os filtros aplicados.`,
        state: true,
      });
    } else {
      urlDonwloadReports.forEach((url) => window.open(url, "_blank"));
    }
  };

  useEffect(() => {
    get();
  }, []);

  return (
    <>
      <Header />
      <Loading mode="blurry" hidden={!showLoading} />

      <Box sx={{ bgcolor: "background.default", minHeight: "100vh", pb: 5 }}>
        <Container
          style={{ maxWidth: "1440px", margin: "0 auto", paddingTop: "32px" }}
        >
          {/* HEADER DA PÁGINA */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            spacing={2}
            mb={4}
            px={1}
          >
            <Box>
              <Typography
                variant="h4"
                fontWeight="800"
                color="text.primary"
                letterSpacing="-0.5px"
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <Assessment color="primary" fontSize="large" /> Gerenciamento de
                Pesquisa
              </Typography>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                fontWeight="500"
                mt={0.5}
              >
                Acompanhe e administre os relatórios analíticos por cliente
              </Typography>
            </Box>

            <Button
              variant="contained"
              color="primary"
              disabled={
                !clientReports?.relatorioClientes ||
                clientReports.relatorioClientes.length === 0
              }
              onClick={onGenerateReport}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                px: 3,
                fontWeight: "bold",
                boxShadow: 2,
              }}
            >
              Gerar Relatório
            </Button>
          </Stack>

          <Card elevation={0} sx={modernCardStyle}>
            {showLoading ? (
              <Loading hidden={!showLoading} />
            ) : (
              <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
                {/* BARRA DE FILTROS & CHIPS */}
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems="center"
                  mb={3}
                  spacing={2}
                >
                  <Box display="flex" flexWrap="wrap" gap={1} flex={1}>
                    {chipDataList?.map((data) => (
                      <Chip
                        key={data.id}
                        label={data.value}
                        color="primary"
                        variant="outlined"
                        onDelete={() => deleteFilter(data)}
                        sx={{ fontWeight: "bold", borderRadius: 2 }}
                      />
                    ))}
                    {chipDataList.length === 0 && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontStyle: "italic", alignSelf: "center" }}
                      >
                        Nenhum filtro aplicado.
                      </Typography>
                    )}
                  </Box>
                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={handleOpen}
                    startIcon={<FilterAlt />}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      color: "text.primary",
                      borderColor: "divider",
                    }}
                  >
                    Filtrar Dados
                  </Button>
                </Stack>

                <Divider sx={{ mb: 3, borderColor: "divider" }} />

                {/* ABAS PRINCIPAIS */}
                <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
                  <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    textColor="primary"
                    indicatorColor="primary"
                  >
                    <Tab
                      label={`NoTouch (${totalNoTouch})`}
                      sx={{
                        fontWeight: 600,
                        textTransform: "none",
                        fontSize: "0.95rem",
                      }}
                    />
                    <Tab
                      label={`Flex (${totalFlex})`}
                      sx={{
                        fontWeight: 600,
                        textTransform: "none",
                        fontSize: "0.95rem",
                      }}
                    />
                  </Tabs>
                </Box>

                {/* LISTAGEM EM ACORDEÕES */}
                <Box>
                  {clientReports?.relatorioClientes.map((relatorio) => {
                    const analysesToShow =
                      activeTab === 0
                        ? relatorio.analisesNoTouch
                        : relatorio.analisesFlex;

                    if (analysesToShow.length === 0) {
                      return null;
                    }

                    const comDocumentoCount = analysesToShow.filter(
                      (item) => item.tipoAnalise === "ComDocumentacao",
                    ).length;

                    const semDocumentoCount = analysesToShow.filter(
                      (analise) => analise.tipoAnalise === "SemDocumentacao",
                    ).length;

                    const simplificadaCount = analysesToShow.filter(
                      (analise) => analise.tipoAnalise === "Simplificada",
                    ).length;

                    return (
                      <Accordion
                        key={relatorio.nomeCliente}
                        expanded={expanded === relatorio.nomeCliente}
                        onChange={handleChange(relatorio.nomeCliente)}
                        sx={accordionStyle}
                        disableGutters
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{ px: { xs: 2, sm: 3 }, py: 1 }}
                        >
                          {/* DISTRIBUIÇÃO PROPORCIONAL DOS CONTADORES */}
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            justifyContent="space-between"
                            alignItems={{ xs: "flex-start", sm: "center" }}
                            width="100%"
                            spacing={{ xs: 1, sm: 0 }}
                          >
                            <Typography
                              variant="subtitle1"
                              fontWeight="bold"
                              color="text.primary"
                              sx={{ width: { xs: "100%", sm: "40%" } }}
                            >
                              {relatorio.nomeCliente}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ width: { xs: "100%", sm: "20%" } }}
                            >
                              <strong>Sem Doc:</strong> {semDocumentoCount}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ width: { xs: "100%", sm: "20%" } }}
                            >
                              <strong>Com Doc:</strong> {comDocumentoCount}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ width: { xs: "100%", sm: "20%" } }}
                            >
                              <strong>Pré-análise:</strong> {simplificadaCount}
                            </Typography>
                          </Stack>
                        </AccordionSummary>

                        <AccordionDetails
                          sx={{ p: 0, bgcolor: "background.paper" }}
                        >
                          <TableContainer>
                            <Table
                              sx={{ minWidth: 650 }}
                              aria-label="tabela de analises do cliente"
                            >
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={tableHeaderStyle} width="5%">
                                    #
                                  </TableCell>
                                  <TableCell sx={tableHeaderStyle} width="20%">
                                    Cliente (CNPJ/CPF)
                                  </TableCell>
                                  <TableCell sx={tableHeaderStyle} width="15%">
                                    Data Inclusão
                                  </TableCell>
                                  <TableCell sx={tableHeaderStyle} width="25%">
                                    Usuário Inclusão
                                  </TableCell>
                                  <TableCell
                                    sx={tableHeaderStyle}
                                    width="20%"
                                    align="center"
                                  >
                                    Status
                                  </TableCell>
                                  <TableCell sx={tableHeaderStyle} width="15%">
                                    Tipo Análise
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {analysesToShow.map((analise, index) => (
                                  <TableRow
                                    key={`${analise.cnpjOuCpf}-${index}`}
                                    hover
                                    sx={{ transition: "background-color 0.2s" }}
                                  >
                                    <TableCell sx={{ color: "text.secondary" }}>
                                      {index + 1}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        fontWeight: 600,
                                        color: "text.primary",
                                      }}
                                    >
                                      {cnpjOrCpfMask(analise.cnpjOuCpf)}
                                    </TableCell>
                                    <TableCell sx={{ color: "text.secondary" }}>
                                      {moment(analise.dataInclusao).format(
                                        "DD/MM/YYYY",
                                      )}
                                    </TableCell>
                                    <TableCell sx={{ color: "text.secondary" }}>
                                      {analise.usuarioInclusao}
                                    </TableCell>
                                    {/* COLUNA DE STATUS COM LARGURA MÁXIMA E TOOLTIP */}
                                    <TableCell align="center">
                                      <Tooltip
                                        title={analise.status}
                                        placement="top"
                                        arrow
                                      >
                                        <Chip
                                          label={analise.status}
                                          color={
                                            analise.status === "Concluída"
                                              ? "success"
                                              : "warning"
                                          }
                                          size="small"
                                          sx={{
                                            fontWeight: "bold",
                                            borderRadius: 1.5,
                                            px: 1,
                                            maxWidth: "200px", // Limita o tamanho máximo do Chip
                                            "& .MuiChip-label": {
                                              whiteSpace: "nowrap",
                                              overflow: "hidden",
                                              textOverflow: "ellipsis", // Adiciona os "..." no final
                                            },
                                          }}
                                        />
                                      </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        size="small"
                                        label={analise.tipoAnalise}
                                        sx={{
                                          bgcolor: "action.hover",
                                          fontWeight: 600,
                                          color: "text.secondary",
                                        }}
                                      />
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </Box>
              </CardContent>
            )}
          </Card>
        </Container>
      </Box>

      {/* MODAL DE FILTROS */}
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", md: "60%" },
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: "16px",
            outline: "none",
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <Typography variant="h5" fontWeight="bold">
              Filtrar Dados
            </Typography>
            <IconButton onClick={handleClose} sx={{ bgcolor: "action.hover" }}>
              <Close />
            </IconButton>
          </Stack>
          <Box>
            <FilterForm
              onSave={(event) => searchFilter(event)}
              dataFilter={dataFilter}
            />
          </Box>
        </Box>
      </Modal>

      <Error
        hasOpen={errorApi.state}
        handleClose={() => setErrorApi({ ...errorApi, state: false })}
        errorMsg={errorApi.message}
        title={errorApi.title}
      />
    </>
  );
};
